/** Client of the identity service (apps/identity-service): company SSO setup and sign-in discovery.
 * Calls carry the same credentials as the Solar API; the service asks the API who the caller is. */
import { authHeaders } from "../auth/auth";
import { config } from "../config";
import { ApiError } from "./client";

export type DomainStatus = "pending" | "verified" | "lost";
export type ConnectionStatus = "draft" | "configured" | "tested" | "active" | "disabled";
export type MigrationState = "pending_removal" | "pending_relink" | "relinked" | "break_glass" | "failed";

export interface SsoDomain {
  id: string;
  domain: string;
  status: DomainStatus;
  txt_name: string;
  txt_value: string;
  verified_at: string | null;
  last_checked_at: string | null;
  last_check_error: string | null;
  check?: { found: boolean; error: string | null };
}

export interface SsoCertificate {
  id: string;
  sha256: string;
  subject: string;
  not_before: string;
  not_after: string;
  in_use: boolean;
}

/** Roles people get when they join by SSO. Admin only through a mapping; owner never. */
export type JoinRole = "viewer" | "sales" | "designer";
export type MappedRole = JoinRole | "admin";
export type MappingClaim = "groups" | "roles";

export interface RoleMapping {
  id?: string;
  claim: MappingClaim;
  value: string;
  role: MappedRole;
}

export interface SsoConnection {
  id: string;
  name: string;
  status: ConnectionStatus;
  idp_entity_id: string | null;
  sso_url: string | null;
  metadata_source: "xml" | "url" | null;
  metadata_url: string | null;
  metadata_refreshed_at: string | null;
  metadata_error: string | null;
  directory_federation_id: string | null;
  directory_domains: string[];
  attached_flows: string[];
  tested_at: string | null;
  activated_at: string | null;
  last_error: string | null;
  certificates: SsoCertificate[];
  default_role: JoinRole;
  role_mappings: RoleMapping[];
  certificate_expires_at: string | null;
  certificate_expiring: boolean;
  last_test: { id: string; email: string; status: "started" | "passed" | "failed"; error: string | null; completed_at: string | null } | null;
  /** TXT records External ID needs before it federates a domain the IdP's sign-in URL is not under. */
  direct_fed_txt?: { name: string; type: string; value: string }[];
}

export interface SsoOverview {
  org_id: string;
  domains: SsoDomain[];
  connection: SsoConnection | null;
  policy: { break_glass_emails: string[] };
  roles: { default_choices: JoinRole[]; mapping_enabled: boolean; mapping_choices: MappedRole[]; mapping_claims: MappingClaim[] };
  service_provider: { entity_id: string; reply_url: string; name_id_format: string; required_claims: { name: string; value: string }[]; sign: string };
  migrations: Partial<Record<MigrationState, number>>;
  test_flow_separate: boolean;
}

export interface Person {
  email: string;
  role: string;
  user_id: string;
}

export interface ActivationPreview {
  connection_id: string;
  status: ConnectionStatus;
  domains: string[];
  moving: Person[];
  break_glass: Person[];
  guests: Person[];
  problems: string[];
}

export interface Migration {
  id: string;
  email: string;
  role: string | null;
  state: MigrationState;
  detail: string | null;
  relinked_at: string | null;
  removed_at: string | null;
}

export interface AuditEvent {
  id: number;
  actor: string;
  action: string;
  target: string | null;
  details: Record<string, unknown>;
  created_at: string;
}

/** `unknown`: the identity service was rate limited, down or slow, so we cannot tell. Never treat it as "password": an
 * SSO user sent to the normal sign-in has no password there and would be stuck. */
export type Discovery = { type: "password" } | { type: "unknown" } | { type: "sso"; connection: string; domain_hint: string; login_url: string };

export const identityEnabled = () => !!config.identityUrl;

async function call<T>(path: string, init: RequestInit = {}, authenticated = true, timeoutMs?: number): Promise<T> {
  const headers = new Headers(init.headers);
  if (authenticated) for (const [k, v] of Object.entries(await authHeaders())) headers.set(k, v);
  if (init.body && typeof init.body === "string") headers.set("Content-Type", "application/json");
  const response = await fetch(`${config.identityUrl}${path}`, { ...init, headers, ...(timeoutMs ? { signal: AbortSignal.timeout(timeoutMs) } : {}) });
  if (!response.ok) {
    let problem: ApiError["problem"] = {}; // not JSON: our generic sentence, never the gateway's words
    try {
      problem = await response.json();
    } catch {
      /* not JSON */
    }
    throw new ApiError(response.status, problem);
  }
  return (response.status === 204 ? undefined : await response.json()) as T;
}

const base = (orgId: string) => `/v1/orgs/${orgId}/sso`;
const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });

export const identity = {
  overview: (orgId: string) => call<SsoOverview>(base(orgId)),
  addDomain: (orgId: string, domain: string) => call<SsoDomain>(`${base(orgId)}/domains`, json("POST", { domain })),
  verifyDomain: (orgId: string, id: string) => call<SsoDomain>(`${base(orgId)}/domains/${id}/verify`, { method: "POST" }),
  removeDomain: (orgId: string, id: string) => call<void>(`${base(orgId)}/domains/${id}`, { method: "DELETE" }),
  saveMetadata: (orgId: string, body: { name?: string; metadata_xml?: string; metadata_url?: string }) =>
    call<SsoConnection>(`${base(orgId)}/connection`, json("PUT", body)),
  putRoles: (orgId: string, body: { default_role?: JoinRole; mappings?: RoleMapping[] }) =>
    call<SsoConnection>(`${base(orgId)}/connection/roles`, json("PUT", body)),
  startTest: (orgId: string, email: string, returnUrl: string) =>
    call<{ test_id: string; authorize_url: string }>(`${base(orgId)}/connection/test`, json("POST", { email, return_url: returnUrl })),
  preview: (orgId: string, breakGlass?: string[]) =>
    call<ActivationPreview>(`${base(orgId)}/activation${breakGlass ? `?break_glass=${encodeURIComponent(breakGlass.join(","))}` : ""}`),
  activate: (orgId: string, breakGlass: string[]) =>
    call<SsoOverview>(`${base(orgId)}/activate`, json("POST", { confirm: true, break_glass_emails: breakGlass })),
  deactivate: (orgId: string) => call<SsoOverview>(`${base(orgId)}/deactivate`, json("POST", { confirm: true })),
  migrations: (orgId: string) => call<{ items: Migration[] }>(`${base(orgId)}/migrations`),
  audit: (orgId: string) => call<{ items: AuditEvent[] }>(`${base(orgId)}/audit?limit=30`),
  /** Public: how this email signs in. A rejected address (4xx other than 429) means "password"; a rate limit, a
   * server error, a network error or no answer within 5 s means "unknown". */
  discover: async (email: string): Promise<Discovery> => {
    if (!config.identityUrl) return { type: "password" };
    try {
      return await call<Discovery>(`/v1/discovery?email=${encodeURIComponent(email)}`, {}, false, 5000);
    } catch (e) {
      if (e instanceof ApiError && e.status >= 400 && e.status < 500 && e.status !== 429) return { type: "password" };
      return { type: "unknown" };
    }
  },
};
